import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, ChevronLeft, ChevronRight, PackageSearch, Download, FileUp, ImageUp } from "lucide-react";
import { toast } from "sonner";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { adminApi } from "@/lib/adminAuth";
import { useDebounced } from "@/lib/useDebounced";
import { formatFechaHora, nombreTipo } from "@/lib/format";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/common/ErrorState";

const PAGE_SIZE = 25;

export default function UnidadesList() {
  usePageTitle("Unidades");
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [etapaId, setEtapaId] = useState("");
  const [tipo, setTipo] = useState("");
  const [page, setPage] = useState(1);
  const qDebounced = useDebounced(q, 300);

  useEffect(() => setPage(1), [qDebounced, clienteId, etapaId, tipo]);

  const { data: etapas = [] } = useQuery({
    queryKey: ["etapas"],
    queryFn: () => adminApi.get("/etapas").then((r) => r.data),
  });
  const { data: tipos = [] } = useQuery({
    queryKey: ["tipos"],
    queryFn: () => adminApi.get("/tipos").then((r) => r.data),
  });
  const { data: clientes = [] } = useQuery({
    queryKey: ["admin-clientes-opciones"],
    queryFn: () => adminApi.get("/admin/clientes/opciones").then((r) => r.data),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin-unidades", qDebounced, clienteId, etapaId, tipo, page],
    queryFn: () =>
      adminApi
        .get("/admin/unidades", {
          params: {
            q: qDebounced || undefined,
            cliente_id: clienteId || undefined,
            etapa_id: etapaId || undefined,
            tipo: tipo || undefined,
            page,
            page_size: PAGE_SIZE,
          },
        })
        .then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const exportar = async () => {
    try {
      const resp = await adminApi.get("/admin/unidades/export", {
        params: { q: qDebounced || undefined, cliente_id: clienteId || undefined, etapa_id: etapaId || undefined, tipo: tipo || undefined },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "unidades.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo exportar el listado. Intentá de nuevo.");
    }
  };

  const totalPaginas = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="mx-auto max-w-6xl px-8 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[var(--tc-text)]">Unidades</h1>
          <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">
            {data ? (
              `${data.total} unidad${data.total === 1 ? "" : "es"}`
            ) : (
              <span className="inline-block h-3.5 w-20 animate-pulse rounded bg-[var(--tc-border)]" />
            )}
          </p>
        </div>
        <div className="flex gap-2.5">
          <button
            data-testid={ADMIN.unidadesExportButton}
            onClick={exportar}
            className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--tc-text-secondary)] transition-colors hover:bg-[var(--tc-surface)]"
          >
            <Download size={16} /> Exportar CSV
          </button>
          <button
            data-testid={ADMIN.unidadesImportFotosButton}
            onClick={() => navigate("/admin/unidades/fotos")}
            className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--tc-text-secondary)] transition-colors hover:bg-[var(--tc-surface)]"
          >
            <ImageUp size={16} /> Importar fotos
          </button>
          <button
            data-testid={ADMIN.unidadesImportButton}
            onClick={() => navigate("/admin/unidades/importar")}
            className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--tc-text-secondary)] transition-colors hover:bg-[var(--tc-surface)]"
          >
            <FileUp size={16} /> Importar planilla
          </button>
          <button
            data-testid={ADMIN.unidadesNewButton}
            onClick={() => navigate("/admin/unidades/nueva")}
            className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)]"
          >
            <Plus size={16} /> Nueva unidad
          </button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2.5">
        <div className="relative">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tc-text-secondary)]" />
          <input
            data-testid={ADMIN.unidadesSearchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por VIN"
            className="w-52 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white py-2 pl-8 pr-3 text-sm outline-none focus:border-[var(--tc-accent)]"
          />
        </div>
        <select
          data-testid={ADMIN.unidadesFilterCliente}
          value={clienteId}
          onChange={(e) => setClienteId(e.target.value)}
          className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-2 text-sm text-[var(--tc-text)] outline-none focus:border-[var(--tc-accent)]"
        >
          <option value="">Todos los clientes</option>
          {clientes.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}</option>
          ))}
        </select>
        <select
          data-testid={ADMIN.unidadesFilterEtapa}
          value={etapaId}
          onChange={(e) => setEtapaId(e.target.value)}
          className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-2 text-sm text-[var(--tc-text)] outline-none focus:border-[var(--tc-accent)]"
        >
          <option value="">Todas las etapas</option>
          {etapas.map((e) => (
            <option key={e.id} value={e.id}>{e.nombre}</option>
          ))}
        </select>
        <select
          data-testid={ADMIN.unidadesFilterTipo}
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-2 text-sm text-[var(--tc-text)] outline-none focus:border-[var(--tc-accent)]"
        >
          <option value="">Todos los tipos</option>
          {tipos.map((t) => (
            <option key={t.id} value={t.id}>{t.nombre}</option>
          ))}
        </select>
      </div>

      <div className="mt-4 overflow-x-auto rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
        {isLoading ? (
          <div className="space-y-2 p-4" data-testid="unidades-list-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full rounded-[var(--tc-radius)]" />
            ))}
          </div>
        ) : isError ? (
          <ErrorState testId="admin-unidades-error" mensaje="No se pudo cargar el listado de unidades." onRetry={refetch} />
        ) : !data || data.resultados.length === 0 ? (
          <div data-testid={ADMIN.unidadesEmptyState} className="flex flex-col items-center py-16 text-center">
            <PackageSearch size={26} className="text-[var(--tc-state-pending)]" />
            <p className="mt-3 text-sm text-[var(--tc-text-secondary)]">No hay unidades con esos filtros.</p>
          </div>
        ) : (
          <table className="w-full min-w-[760px] text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <thead>
              <tr className="border-b border-[var(--tc-border)] text-left text-xs font-medium uppercase tracking-wide text-[var(--tc-text-secondary)]">
                <th className="px-4 py-3">VIN</th>
                <th className="px-4 py-3">Marca / Modelo</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Etapa actual</th>
                <th className="px-4 py-3">Actualizado</th>
              </tr>
            </thead>
            <tbody>
              {data.resultados.map((u) => (
                <tr
                  key={u.id}
                  data-testid={ADMIN.unidadesRow}
                  onClick={() => navigate(`/admin/unidades/${u.id}`)}
                  className="cursor-pointer border-b border-[var(--tc-border)] last:border-0 transition-colors hover:bg-[var(--tc-surface)]"
                >
                  <td className="px-4 py-3 font-mono text-xs tabular-nums text-[var(--tc-text-secondary)]">
                    {u.vin.slice(0, -6)}
                    <strong className="text-[var(--tc-text)]">{u.vin_last6}</strong>
                  </td>
                  <td className="px-4 py-3 font-medium text-[var(--tc-text)]">{u.marca} {u.modelo}</td>
                  <td className="px-4 py-3 text-[var(--tc-text-secondary)]">{nombreTipo(tipos, u.tipo)}</td>
                  <td className="px-4 py-3 text-[var(--tc-text-secondary)]">{u.cliente?.nombre || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-[var(--tc-accent-soft)] px-2.5 py-1 text-xs font-medium text-[var(--tc-accent)]">
                      {u.etapa_actual?.nombre || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs tabular-nums text-[var(--tc-text-secondary)]">{formatFechaHora(u.actualizado_en)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-[var(--tc-text-secondary)]">Página {page} de {totalPaginas}</p>
          <div className="flex gap-2">
            <button
              data-testid={ADMIN.unidadesPaginationPrev}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="flex items-center gap-1 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-1.5 text-sm disabled:opacity-40"
            >
              <ChevronLeft size={14} /> Anterior
            </button>
            <button
              data-testid={ADMIN.unidadesPaginationNext}
              disabled={page >= totalPaginas}
              onClick={() => setPage((p) => p + 1)}
              className="flex items-center gap-1 rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white px-3 py-1.5 text-sm disabled:opacity-40"
            >
              Siguiente <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
